import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, RefreshCcw, Search } from "lucide-react";
import { listCampaigns, type Campaign, type CampaignStatus } from "@/api/email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CampaignList } from "@/components/email/CampaignList";
import { NewCampaignDialog } from "@/components/email/NewCampaignDialog";
import { ScopeBadge } from "@/components/email/EmailBadges";
import { useDebounce } from "@/hooks/useDebounce";
import { useEmailProjects } from "@/hooks/useEmailProjects";
import { campaignPath } from "@/lib/emailRoutes";
import { cn } from "@/lib/utils";
import { useEmailOutlet } from "@/pages/email/EmailLayout";

const ALL = "all";
type StatusFilter = "open" | "all" | CampaignStatus;
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "open", label: "Not archived" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "DRAFT", label: "Draft" },
  { value: "ARCHIVED", label: "Archived" },
  { value: "all", label: "All statuses" },
];

/**
 * Every campaign the user may open. With Email in several projects, "All"
 * merges each project's own list — one call per project, each checked there.
 */
export default function CampaignsPage() {
  const navigate = useNavigate();
  const { slug, meta } = useEmailOutlet();
  const { projects } = useEmailProjects();
  const [params, setParams] = useSearchParams();

  const multi = projects.length > 1;
  const projectParam = params.get("project");
  const project = multi && projectParam && (projectParam === ALL || projects.some((p) => p.project.slug === projectParam))
    ? projectParam
    : slug;
  const status = (STATUS_OPTIONS.find((o) => o.value === params.get("status"))?.value ?? "open") as StatusFilter;
  const [search, setSearch] = useState(params.get("q") ?? "");
  const debounced = useDebounce(search, 200);

  const key = project === ALL ? projects.map((p) => p.project.slug).join(",") : project;
  const [loaded, setLoaded] = useState<{ key: string; items: Campaign[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all(key.split(",").map((s) => listCampaigns(s)))
      .then((lists) => {
        if (cancelled) return;
        const items = lists.flat().sort((a, b) => b.updated_at.localeCompare(a.updated_at));
        setLoaded({ key, items });
      })
      .catch(() => !cancelled && setError("Campaigns could not be loaded."));
    return () => {
      cancelled = true;
    };
  }, [key, reload]);

  function update(next: Record<string, string | null>) {
    const merged = new URLSearchParams(params);
    for (const [name, value] of Object.entries(next)) {
      if (value === null || value === "") merged.delete(name);
      else merged.set(name, value);
    }
    setParams(merged, { replace: true });
  }

  useEffect(() => {
    const current = params.get("q") ?? "";
    if (debounced.trim() !== current) update({ q: debounced.trim() || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the URL follows the debounced search only
  }, [debounced]);

  const campaigns = loaded && loaded.key === key ? loaded.items : null;
  const term = debounced.trim().toLowerCase();
  const shown = campaigns?.filter(
    (c) =>
      (status === "all" || (status === "open" ? c.status !== "ARCHIVED" : c.status === status)) &&
      (!term || c.name.toLowerCase().includes(term)),
  ) ?? null;
  const filtered = !!term || status !== "open";
  const accessOf = (campaign: Campaign) =>
    projects.find((p) => p.project.id === campaign.project_id)?.project ??
    (meta?.project.id === campaign.project_id ? meta.project : null);
  const canCreate = !!meta?.project.can_manage;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto sm:min-w-[160px] sm:max-w-xs sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search a campaign…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 rounded-lg border-slate-200 bg-white pl-9"
            aria-label="Search campaigns"
          />
        </div>
        <Select value={status} onValueChange={(value) => update({ status: value === "open" ? null : value })}>
          <SelectTrigger className="h-9 w-[140px] rounded-lg border-slate-200 bg-white" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {multi && (
          <Select value={project} onValueChange={(value) => update({ project: value === slug ? null : value })}>
            <SelectTrigger className="h-9 w-[180px] rounded-lg border-slate-200 bg-white" aria-label="Project">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.project.slug} value={p.project.slug}>
                  <span className="inline-flex items-center gap-1.5">
                    <ScopeBadge scope={p.project.scope} />
                    {p.project.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-slate-500"
          onClick={() => setReload((n) => n + 1)}
          aria-label="Refresh"
        >
          <RefreshCcw className={cn("h-3.5 w-3.5", campaigns === null && !error && "animate-spin")} />
        </Button>
        <div className="ml-auto">
          {canCreate && (
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Create campaign
            </Button>
          )}
        </div>
      </div>

      <CampaignList
        campaigns={shown}
        error={error}
        filtered={filtered}
        showProject={project === ALL}
        accessFor={accessOf}
        external={!!meta?.external_effects}
        canCreate={canCreate}
        onCreate={() => setNewOpen(true)}
        onChanged={() => setReload((n) => n + 1)}
      />

      {meta && (
        <NewCampaignDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          slug={slug}
          access={meta.project}
          onCreated={(created) => navigate(campaignPath(created.project_slug, created.id))}
        />
      )}
    </div>
  );
}
